import { AxiosError } from 'axios';
import { assign, fromPromise, sendTo, setup } from 'xstate';
import { sendAxiosErrorAlert } from '~/features/utils';
import { EMPLOYER_ASSOCIATION_STATE, USER_STATE } from '~/features/xstateSystem';
import { ProviderAssociationHistoryEntry, ProviderEmployerAssociation } from '~/types';
import { disassociate, getCurrentAssociation, getProviderAssociationHistory } from './api';

type Context = {
  association: ProviderEmployerAssociation | null;
  // [MCV-1927] Previous employers; null until the first load answers.
  history: ProviderAssociationHistoryEntry[] | null;
  historyFailed: boolean;
};

type Events =
  | { type: 'ON_LOAD' }
  | { type: 'ON_DISASSOCIATE' }
  | { type: 'ON_SEND_DISASSOCIATE_CONFIRMATION' }
  | { type: 'ON_WEBSOCKET_MESSAGE' }; // New event for websocket messages

type InputArgs = { input: { context: Context; event: Events } };

const loadCurrent = async () => {
  try {
    return await getCurrentAssociation();
  } catch (err) {
    if (err instanceof AxiosError && err.response?.status === 404) {
      return null;
    }
    throw err;
  }
};

// [MCV-1927] A failed history read must not hide the current association: it comes back as
// `history: null` and the section says the list could not be loaded.
const loadHistory = async (): Promise<Pick<Context, 'history' | 'historyFailed'>> => {
  try {
    return { history: await getProviderAssociationHistory(), historyFailed: false };
  } catch {
    return { history: null, historyFailed: true };
  }
};

// A failed current-association read must not discard a history that loaded: each result is
// kept, and the current-association error comes back as `associationError` for the alert.
const settleCurrent = async (): Promise<{
  association: ProviderEmployerAssociation | null;
  associationError?: unknown;
}> => {
  try {
    return { association: await loadCurrent() };
  } catch (associationError) {
    return { association: null, associationError };
  }
};

const API_CALLS = {
  loadAssociation: async (_: InputArgs) => {
    const [current, history] = await Promise.all([settleCurrent(), loadHistory()]);
    return { ...current, ...history };
  },
  disassociate: async (_: InputArgs) => {
    return disassociate();
  },
};

export const employerAssociationMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
  actors: {
    loadAssociation: fromPromise(API_CALLS.loadAssociation),
    disassociate: fromPromise(API_CALLS.disassociate),
  },
  actions: {
    openDisassociateConfirmation: ({ system, context, self }) => {
      const employerName = context.association?.employer?.employerName ?? 'this employer';
      const onConfirm = () => {
        // Use self to send event back to this machine
        self.send({ type: 'ON_DISASSOCIATE' });
      };

      // Send confirmation to userState (which handles the confirmation dialog)
      const userActor = system.get('userState');
      if (userActor) {
        userActor.send({
          type: 'ON_SHOW_CONFIRMATION',
          payload: {
            title: 'Disassociate from Employer',
            message: `Are you sure you want to disassociate from ${employerName}? They will no longer be able to manage your vault.`,
            confirmLabel: 'Disassociate',
            onConfirm: onConfirm,
          },
        });
      } else {
        console.warn(
          '[employerAssociation] Unable to send confirmation - userState not found in system'
        );
      }
    },
    logWebSocketMessage: () => {
      console.log('[employerAssociation] ON_WEBSOCKET_MESSAGE received - triggering reload');
    },
  },
}).createMachine({
  id: EMPLOYER_ASSOCIATION_STATE,
  systemId: EMPLOYER_ASSOCIATION_STATE,
  context: ({ input }) => ({
    association: input?.association ?? null,
    history: input?.history ?? null,
    historyFailed: false,
  }),
  initial: '$_IDLE',
  on: {
    ON_WEBSOCKET_MESSAGE: {
      target: '.$_LOADING',
      actions: [() => console.log('triggered ON_WEBSOCKET_MESSAGE for employerAssociationState')],
    },
  },
  states: {
    $_IDLE: {
      on: {
        ON_LOAD: { target: '$_LOADING' },
      },
    },
    $_LOADING: {
      invoke: {
        src: 'loadAssociation',
        input: ({ context, event }) => ({ context, event }),
        onDone: [
          {
            target: '$_EMPTY',
            guard: ({ event }) => event.output.associationError !== undefined,
            actions: [
              assign({
                association: null,
                history: ({ context, event }) => event.output.history ?? context.history,
                historyFailed: ({ event }) => event.output.historyFailed,
              }),
              sendTo(
                ({ system }) => system.get(USER_STATE),
                ({ event }) => sendAxiosErrorAlert({ error: event.output.associationError })
              ),
            ],
          },
          {
            target: '$_ASSOCIATED',
            guard: ({ event }) => event.output.association !== null,
            actions: assign({
              association: ({ event }) => event.output.association,
              history: ({ context, event }) => event.output.history ?? context.history,
              historyFailed: ({ event }) => event.output.historyFailed,
            }),
          },
          {
            target: '$_EMPTY',
            actions: assign({
              association: null,
              history: ({ context, event }) => event.output.history ?? context.history,
              historyFailed: ({ event }) => event.output.historyFailed,
            }),
          },
        ],
        // Defensive: loadAssociation settles both reads, so it should never reject. If it ever
        // does, keep whatever history is already held.
        onError: {
          target: '$_EMPTY',
          actions: [
            assign({ association: null }),
            sendTo(
              ({ system }) => system.get(USER_STATE),
              ({ event }) => sendAxiosErrorAlert(event)
            ),
          ],
        },
      },
    },

    $_ASSOCIATED: {
      on: {
        ON_LOAD: { target: '$_LOADING' },
        ON_DISASSOCIATE: { target: '$_DISASSOCIATING' },
        ON_SEND_DISASSOCIATE_CONFIRMATION: { actions: 'openDisassociateConfirmation' },
      },
    },

    $_EMPTY: {
      on: {
        ON_LOAD: { target: '$_LOADING' },
      },
    },

    $_DISASSOCIATING: {
      invoke: {
        src: 'disassociate',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          // [MCV-1927] Reload so the employer that was just left shows under Previous Employers.
          target: '$_LOADING',
          actions: [
            assign({ association: null }),
            sendTo(
              ({ system }) => system.get(USER_STATE),
              () => ({
                type: 'ON_SHOW_ALERT' as const,
                payload: {
                  text: 'Successfully disassociated from employer',
                  type: 'success' as const,
                  timeout: 4000,
                },
              })
            ),
          ],
        },
        onError: {
          target: '$_ASSOCIATED',
          actions: sendTo(
            ({ system }) => system.get(USER_STATE),
            ({ event }) => sendAxiosErrorAlert(event)
          ),
        },
      },
    },
  },
});
